import React, { useMemo } from "react";
import { useApp } from "../../context/AppState";
import {
  CART_DRAWER_WATERMARK_COMPACT,
  getCartDrawerWatermarkPositionStyle,
  getEditorWatermarkHref,
  getLiveWatermarkHref,
  getOutsideWatermarkPositionStyle,
  isWatermarkEnabled,
  resolveWatermarkDisplay,
  shouldShowPopupWatermark,
} from "../../utils/popup-watermark";

const PopupWatermark = ({
  popupData,
  activeStep,
  visible = true,
  variant = "default",
}) => {
  const { appState } = useApp();
  const config = useMemo(() => resolveWatermarkDisplay(), []);

  const shopHint = useMemo(() => {
    const shop = appState?.shop?.shop || appState?.shop;
    const name = shop?.name || shop?.myshopify_domain || shop?.domain;
    return name ? `Store: ${name}` : "";
  }, [appState?.shop]);

  const href = useMemo(() => {
    if (appState?.is_preview_mode) {
      return getEditorWatermarkHref(shopHint);
    }
    return getLiveWatermarkHref();
  }, [appState?.is_preview_mode, shopHint]);

  if (!visible) return null;

  if (
    !shouldShowPopupWatermark(
      popupData,
      activeStep,
      appState?.watermark,
      appState?.is_preview_mode,
      appState?.shop
    )
  ) {
    return null;
  }

  if (!isWatermarkEnabled(appState?.watermark, appState?.shop)) {
    return null;
  }

  const onBadgeClick = (e) => {
    e.stopPropagation();
    if (appState?.is_preview_mode) {
      e.preventDefault();
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }
  };

  const title = appState?.is_preview_mode
    ? "Contact WeUpsell to remove or upgrade branding"
    : config.text;

  const isCartDrawer = variant === "cart-drawer";
  const compact = isCartDrawer ? CART_DRAWER_WATERMARK_COMPACT : null;

  return (
    <div
      data-weupsell-popup-watermark="true"
      data-weupsell-watermark-variant={variant}
      style={
        isCartDrawer
          ? getCartDrawerWatermarkPositionStyle()
          : getOutsideWatermarkPositionStyle(config.position, config.offsetGap)
      }
      className={
        isCartDrawer
          ? "weupsell-popup-watermark weupsell-popup-watermark--cart-drawer"
          : "weupsell-popup-watermark"
      }
    >
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={title}
        onClick={onBadgeClick}
        style={{
          textDecoration: "none",
          display: "inline-flex",
          alignItems: "center",
          gap: compact?.gap ?? "6px",
          padding: compact?.padding ?? "5px 8px",
          backgroundColor: "rgba(255, 255, 255, 0.92)",
          border: "1px solid rgba(0, 0, 0, 0.08)",
          borderRadius: compact?.borderRadius ?? "8px",
          boxShadow: isCartDrawer
            ? "0 1px 4px rgba(0, 0, 0, 0.08)"
            : "0 2px 8px rgba(0, 0, 0, 0.12)",
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          fontSize: compact?.fontSize ?? "11px",
          fontWeight: 600,
          lineHeight: 1.2,
          color: "#303030",
          pointerEvents: "auto",
          maxWidth: isCartDrawer ? "100%" : undefined,
        }}
      >
        {config.logoUrl ? (
          <img
            src={config.logoUrl}
            alt=""
            width={compact?.logoSize ?? 18}
            height={compact?.logoSize ?? 18}
            style={{ borderRadius: "4px", flexShrink: 0, display: "block" }}
          />
        ) : null}
        <span style={isCartDrawer ? { whiteSpace: "nowrap" } : undefined}>
          {config.text}
        </span>
      </a>
    </div>
  );
};

export default PopupWatermark;
